<script setup lang="ts">
import type { LikeButtonProps } from './types'
import { LucideHeart } from 'lucide-vue-next'

const { t } = useI18n()

const props = withDefaults(defineProps<LikeButtonProps>(), {
  variant: 'outline',
  likeCount: 0
})

// Like functionality
const { isLiked, toggleLike: toggleLikeAction } = useLikes()
const isLikedState = computed(() => isLiked.value(props.id))

// Local like count that updates on toggle
const currentLikeCount = ref(props.likeCount)

// Watch for props changes to sync external updates
watch(
  () => props.likeCount,
  (newCount) => {
    currentLikeCount.value = newCount
  }
)

const handleToggleLike = async (event: Event) => {
  event.preventDefault()
  event.stopPropagation()

  try {
    const wasLiked = isLikedState.value
    const result = await toggleLikeAction(props.id)

    // Update local count only on successful result
    if (result?.status === 'success') {
      if (wasLiked) {
        currentLikeCount.value = Math.max(0, currentLikeCount.value - 1)
      } else {
        currentLikeCount.value += 1
      }
    }
  } catch (error) {
    console.error('Error toggling like:', error)
  }
}

// Compute button classes based on variant
const buttonClasses = computed(() => {
  const baseClasses =
    'flex items-center gap-2 rounded-full transition-all duration-200 hover:scale-110 active:scale-95'

  if (props.variant === 'outline') {
    return [
      baseClasses,
      'h-10 shrink-0 border border-gray-200 bg-white hover:border-gray-300',
      currentLikeCount.value > 0 ? 'px-3' : 'w-10 justify-center',
      isLikedState.value ? 'border-red-200 text-red-600' : 'text-gray-400'
    ]
  } else {
    // card variant
    return [
      baseClasses,
      'absolute right-3 top-3 z-10 h-8 w-8 justify-center bg-white/90 backdrop-blur-sm hover:bg-white hover:shadow-lg',
      isLikedState.value ? 'text-red-600 shadow-md' : 'text-gray-400'
    ]
  }
})

const iconClasses = computed(() => {
  const baseClasses = 'transition-all duration-200'
  const sizeClass = props.variant === 'outline' ? 'h-5 w-5' : 'h-4 w-4'
  const fillClass = isLikedState.value ? 'fill-current' : ''

  return [baseClasses, sizeClass, fillClass]
})
</script>

<template>
  <ClientOnly>
    <button
      :class="[buttonClasses, props.class]"
      :title="
        isLikedState
          ? t('widget.news_page.details.remove_from_likes')
          : t('widget.news_page.details.add_to_likes')
      "
      :aria-label="
        isLikedState
          ? t('widget.news_page.details.remove_from_likes')
          : t('widget.news_page.details.add_to_likes')
      "
      @click="handleToggleLike"
    >
      <LucideHeart :class="iconClasses" />
      <span v-if="variant === 'outline' && currentLikeCount > 0" class="text-sm">
        {{ currentLikeCount }}
      </span>
    </button>
  </ClientOnly>
</template>
